import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { hideBanner, showBanner } from 'store/banner/reducer';
import { fetchAndSetPoiList } from 'store/userOptions/actions';
import { BannerType } from 'lib/Banner';
import { Color as ChipColor } from 'lib/Chip';
import { cancelFunctions } from 'utils/cancelFunctions';
import useChannels from 'customHooks/useChannels';
import { Store } from 'components/common/types/Store.types';
import useNetworkReferenceData from 'components/pages/Planner/hooks/useNetworkReferenceData';
import useReferenceData from 'components/pages/Planner/hooks/useReferenceData';
import Preloader from 'lib/Preloader';
import {
  clearNewProduct,
  clearProductLines,
  hideSecondaryPanel,
  setIsReadOnly,
  setIsSavingProduct,
  setOriginalProduct,
} from 'store/productManagement/reducer';
import { clearNotification } from 'store/notification/reducer';
import { getIsReadOnly, getNewProduct } from 'store/productManagement/selectors';
import { getMarketId } from 'store/publisher/selectors';
import { NewProduct } from 'store/productManagement/reducer.types';
import { CreateProductContent } from './CreateProductContent/CreateProductContent';
import { CreateProductSidebar } from './CreateProductSidebar.tsx/CreateProductSidebar';
import { useCreateProductTemplate } from '../hooks/useCreateProductTemplate';
import useApplyOriginalProduct from '../hooks/useApplyOriginalProduct';
import ProductLineTabs from './ProductLineTabs';
import { hasUnsavedProductChanges } from './hasUnsavedProductChanges';

const CreateProduct = (): React.ReactElement => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const saveProduct = useCreateProductTemplate();
  const product = useSelector(getNewProduct);
  const {
    name,
    mediaType,
    pricingMode,
    range,
    channels,
    locations,
    formats,
    budget,
    frames,
    impressions,
    networks,
    objectives,
    folder,
    poi,
    frameList,
    tags,
    sot,
    sweep,
  } = product;

  const productLines = useSelector((state: Store) => state.productManagement.productLines);
  const activeLineId = useSelector((state: Store) => state.productManagement.activeLineId);
  const originalProductId = useSelector((state: Store) => state.productManagement.originalProductId);
  const isSavingProduct = useSelector((state: Store) => state.productManagement.isSavingProduct);
  const isReadOnly = useSelector(getIsReadOnly);
  const marketId = useSelector(getMarketId);

  const [initialProduct, setInitialProduct] = useState<NewProduct | null>(null);

  const { applyProduct } = useApplyOriginalProduct();
  const { loadReferenceData } = useReferenceData();

  const clearInitialProduct = (): void => setInitialProduct(null);

  useEffect(() => {
    dispatch(clearNewProduct());
    dispatch(clearProductLines());
    dispatch(hideSecondaryPanel());
    dispatch(clearNotification());

    if (originalProductId) {
      applyProduct(originalProductId);
    }

    return () => {
      dispatch(setOriginalProduct({ originalProductId: undefined, intendedOriginalProductState: undefined }));
      dispatch(setIsSavingProduct(false));
      dispatch(setIsReadOnly(false));
      clearInitialProduct();
    };
  }, []);

  useChannels();
  useEffect(() => {
    loadReferenceData({
      environments: channels?.value || [],
      mediaType: mediaType?.value,
      isProductTemplate: true,
    });
  }, [channels?.value, mediaType?.value]);

  useNetworkReferenceData({
    environments: channels?.value || [],
    mediaType: mediaType?.value,
    isProductTemplate: true,
  });

  useEffect(() => {
    dispatch(fetchAndSetPoiList(cancelFunctions));
  }, [marketId]);

  useEffect(() => {
    if (!initialProduct && product.name) {
      setInitialProduct(product);
    }
  }, [initialProduct, product]);

  const cleanupProductPage = (): void => {
    dispatch(hideBanner());
  };

  const updateProductPageBanner = useCallback((): void => {
    dispatch(
      showBanner({
        type: BannerType.INFO_ALT,
        headerInfo: 'Product Creation Manager',
        chip: isReadOnly
          ? {
              label: 'Read Only',
              color: ChipColor.PURPLE,
              truncateLabel: false,
            }
          : undefined,
        confirmButton: isReadOnly
          ? undefined
          : {
              buttonLabel: 'Save',
              actionCallback: saveProduct,
            },
        backButton: {
          actionCallback: () => {
            cleanupProductPage();
            navigate('/deals/product-management');
          },
          isChange: hasUnsavedProductChanges(initialProduct, product, isReadOnly),
        },
      }),
    );
  }, [initialProduct, product, isReadOnly, saveProduct, navigate]);

  useEffect(() => {
    updateProductPageBanner();

    return () => {
      cleanupProductPage();
    };
  }, [objectives]);

  useEffect(() => {
    updateProductPageBanner();

    return () => {
      dispatch(hideSecondaryPanel());
      cleanupProductPage();
    };
  }, [
    name,
    mediaType,
    pricingMode,
    range,
    channels,
    locations,
    formats,
    budget,
    frames,
    impressions,
    networks,
    folder,
    poi,
    frameList,
    tags,
    sot,
    sweep,
    productLines,
    activeLineId,
  ]);

  return (
    <div className="flex relative flex-1 overflow-hidden h-full">
      <Preloader isLoading={isSavingProduct} />
      <CreateProductSidebar />
      <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
        <div className="px-4 pt-4 w-full">
          <ProductLineTabs />
        </div>
        <div className="flex-1 grid grid-cols-[3fr_2fr]">
          <CreateProductContent key={activeLineId} />
        </div>
      </div>
    </div>
  );
};

export default CreateProduct;
